import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { royai } from "@/lib/royai";
import { toast } from "sonner";
import {
  TrendingUp, CheckCircle2, Circle, AlertCircle, Sparkles, ArrowRight,
  Loader2, Music2, Video, Save,
} from "lucide-react";

// DSP (audio streams) inputs
const DSP_FIELDS = [
  { id: "spotify_streams",       label: "Spotify Streams",       required: true },
  { id: "apple_music_streams",   label: "Apple Music Streams",   required: true },
  { id: "youtube_music_streams", label: "YouTube Music Streams", required: false },
  { id: "soundcloud_plays",      label: "SoundCloud Plays",      required: false },
  { id: "pandora_streams",       label: "Pandora Streams",       required: false },
  { id: "radio_airplay",         label: "Radio Airplay (spins)", required: false },
];

const SHAZAM_TIERS = [
  { min: 5_000_000, label: "ULTIMATE RISK OF LOSS", mult: 5.0, color: "#DC2626" },
  { min: 1_000_000, label: "CRITICAL",              mult: 3.0, color: "#F97316" },
  { min:   250_000, label: "HIGH PRIORITY",         mult: 1.5, color: "#F59E0B" },
  { min:   100_000, label: "MEDIUM PRIORITY",       mult: 1.0, color: "#FACC15" },
];
const shazamTier = (n) => SHAZAM_TIERS.find((t) => n >= t.min) || null;

// UGC / short-form video — pair of (views, videos) per platform
const UGC_PLATFORMS = [
  { key: "tiktok",           label: "TikTok" },
  { key: "youtube_shorts",   label: "YouTube Shorts" },
  { key: "instagram_reels",  label: "Instagram Reels" },
  { key: "facebook_reels",   label: "Facebook Reels" },
];
const UGC_FIELDS = UGC_PLATFORMS.flatMap((p) => ([
  { id: `${p.key}_views`,  label: `${p.label} Views`,  platform: p.label, kind: "views"  },
  { id: `${p.key}_videos`, label: `${p.label} Videos`, platform: p.label, kind: "videos" },
]));

const ALL_FIELDS = [...DSP_FIELDS, ...UGC_FIELDS];

function statusPill(status) {
  if (status === "connected") return { label: "Connected", icon: CheckCircle2, color: "#10B981" };
  if (status === "missing")   return { label: "Missing",   icon: AlertCircle,  color: "#E11D48" };
  return                              { label: "Recommended", icon: Circle,   color: "#F59E0B" };
}

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function RoyaltyAnalyzer() {
  const [form, setForm] = useState(Object.fromEntries(ALL_FIELDS.map((f) => [f.id, ""])));
  const [songTitle, setSongTitle] = useState("");
  const [mainArtist, setMainArtist] = useState("");
  const [shazamCount, setShazamCount] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  const setNum = (k, v) => setForm({ ...form, [k]: v.replace(/[^0-9]/g, "").slice(0, 12) });

  const run = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setSaved(null);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, parseInt(v || "0", 10)]));
      const { data } = await api.post("/royalty-analyzer/analyze", payload);
      setResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const saveToAudit = async () => {
    if (!songTitle.trim() || !mainArtist.trim()) {
      toast.error("Enter both Song Title and Main Artist first.");
      return;
    }
    if (!result?.ugc_summary?.per_platform?.length) {
      toast.error("Run the analysis first so there is data to save.");
      return;
    }
    setSaving(true);
    try {
      const ugc_platforms = result.ugc_summary.per_platform
        .filter((p) => p.views > 0 || p.videos > 0)
        .map((p) => ({ platform: p.platform, views: p.views, videos: p.videos }));
      const data = await royai.saveAnalyzerToAudit({
        song_title: songTitle.trim(),
        main_artist: mainArtist.trim(),
        ugc_platforms,
        shazam_count: parseInt(shazamCount || "0", 10),
      });
      setSaved(data);
      const shazamNote = data.shazam_bounty_created
        ? " · High-priority SoundExchange bounty raised"
        : (data.shazam_count > 0 ? ` · Shazam Insight recorded (${data.shazam_count.toLocaleString()})` : "");
      toast.success(
        `Saved "${data.display_title}" to Deep Meta Audit · ${data.rows.length} platform row${data.rows.length === 1 ? "" : "s"}${shazamNote}`,
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="royalty-analyzer" className="bg-[#0c0c0c] border border-white/5 p-7 lg:p-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-amber-500" />
            <div className="text-overline">Royalty &amp; UGC Opportunity Analyzer<span className="text-amber-500">™</span></div>
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-medium tracking-tight">
            Map streams, Shorts, Reels &amp; TikTok to the collection infrastructure.
          </h2>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Not a calculator. Not a promise of dollars. This analyzes the gap between your reported
            catalog activity — <span className="text-white">audio streams and short-form video views</span> —
            and the platforms that collect on it. UGC views are estimated at $0.50 per 1,000 views.
          </p>
        </div>
      </div>

      <form onSubmit={run} className="space-y-6 mb-6" data-testid="analyzer-form">
        {/* Track identifiers — feed the "Save to Deep Meta Audit" flow */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Music2 size={14} className="text-amber-500" />
            <div className="text-overline">Track Details</div>
            <span className="text-[10px] text-neutral-500 font-mono-tech">used for save + proactive metadata search</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block">Song Title</label>
              <input
                value={songTitle}
                onChange={(e) => setSongTitle(e.target.value.slice(0, 200))}
                placeholder="e.g. Dirt"
                data-testid="input-song_title"
                className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block">Main Artist</label>
              <input
                value={mainArtist}
                onChange={(e) => setMainArtist(e.target.value.slice(0, 200))}
                placeholder="e.g. Key Glock"
                data-testid="input-main_artist"
                className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block flex items-center gap-1.5">
                Shazam Count
                <span className="text-neutral-700 normal-case tracking-normal text-[10px]">· discovery signal</span>
              </label>
              <input
                value={shazamCount}
                onChange={(e) => setShazamCount(e.target.value.replace(/[^0-9]/g, "").slice(0, 12))}
                inputMode="numeric"
                placeholder="e.g. 257044"
                data-testid="input-shazam_count"
                className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500 font-mono-tech"
              />
              {shazamCount && (() => {
                const n = parseInt(shazamCount, 10) || 0;
                const t = shazamTier(n);
                if (!t) return (
                  <div className="text-[10px] text-neutral-600 mt-1">
                    Below 100k — no broadcast bounty raised
                  </div>
                );
                const est = Math.round(n * 0.10 * t.mult);
                return (
                  <div className="text-[10px] mt-1 flex items-center gap-1" style={{ color: t.color }}>
                    <AlertCircle size={10} /> {t.label} · {t.mult}× · est ~${est.toLocaleString()}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* DSP streams block */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Music2 size={14} className="text-amber-500" />
            <div className="text-overline">Audio Streams (DSPs)</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {DSP_FIELDS.map((f) => (
              <div key={f.id}>
                <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block">
                  {f.label}{!f.required && <span className="text-neutral-700"> · optional</span>}
                </label>
                <input value={form[f.id]} onChange={(e) => setNum(f.id, e.target.value)} inputMode="numeric"
                  placeholder="0" data-testid={`input-${f.id}`}
                  className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500 font-mono-tech" />
              </div>
            ))}
          </div>
        </div>

        {/* UGC block */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Video size={14} className="text-amber-500" />
            <div className="text-overline">Short-Form Video Views &amp; Video Counts (UGC)</div>
            <span className="text-[10px] text-neutral-500 font-mono-tech">$0.50 / 1,000 views</span>
          </div>
          <div className="space-y-2">
            <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr] gap-3 px-1 text-[10px] uppercase tracking-widest text-neutral-600">
              <div>Platform</div>
              <div>Views</div>
              <div>Videos</div>
            </div>
            {UGC_PLATFORMS.map((p) => (
              <div key={p.key} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-3 items-center">
                <div className="text-sm text-white sm:pl-1">{p.label}</div>
                <input
                  value={form[`${p.key}_views`]}
                  onChange={(e) => setNum(`${p.key}_views`, e.target.value)}
                  inputMode="numeric"
                  placeholder="views"
                  data-testid={`input-${p.key}_views`}
                  className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500 font-mono-tech"
                />
                <input
                  value={form[`${p.key}_videos`]}
                  onChange={(e) => setNum(`${p.key}_videos`, e.target.value)}
                  inputMode="numeric"
                  placeholder="videos"
                  data-testid={`input-${p.key}_videos`}
                  className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500 font-mono-tech"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={busy} data-testid="run-analyzer-btn"
            className="inline-flex items-center gap-2 bg-amber-500 text-black font-semibold px-6 py-3 hover:brightness-110 transition-all disabled:opacity-50">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Analyzing…</> : <>Run Analysis <ArrowRight size={16} /></>}
          </button>
        </div>
      </form>

      {result && (
        <div className="space-y-px bg-white/5 border border-white/5" data-testid="analyzer-result">
          {/* Catalog Activity + Opportunity Score */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px">
            <div className="bg-[#0A0A0A] p-6">
              <div className="text-overline mb-2">Catalog Activity Summary</div>
              <div className="font-display text-4xl font-medium mb-2" style={{ color: result.catalog_activity.tier.color }}>
                {result.catalog_activity.total_plays.toLocaleString()}
              </div>
              <div className="font-mono-tech text-xs text-neutral-500 mb-3">
                total reported plays
                {result.catalog_activity.total_ugc_views > 0 && (
                  <> · {result.catalog_activity.total_streams.toLocaleString()} streams + {result.catalog_activity.total_ugc_views.toLocaleString()} UGC views</>
                )}
              </div>
              <div className="text-overline" style={{ color: result.catalog_activity.tier.color }}>
                {result.catalog_activity.tier.label}
              </div>
            </div>
            <div className="bg-[#0A0A0A] p-6">
              <div className="text-overline mb-2">Opportunity Score<span className="text-amber-500">™</span></div>
              <div className="font-display text-5xl font-medium mb-2" style={{ color: result.opportunity.color }} data-testid="opportunity-score">
                {result.opportunity.score}<span className="text-xl text-neutral-500"> / 100</span>
              </div>
              <div className="text-overline" style={{ color: result.opportunity.color }}>{result.opportunity.label}</div>
              <p className="text-xs text-neutral-500 mt-3">Based on activity × collection-infrastructure gaps.</p>
            </div>
          </div>

          {/* UGC recoverable estimate — only when views/videos were reported */}
          {result.ugc_summary && (result.ugc_summary.total_views > 0 || result.ugc_summary.total_videos > 0) && (
            <div className="bg-[#0A0A0A] p-6" data-testid="ugc-summary">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Video size={16} className="text-amber-500" />
                <div className="text-overline">UGC Recoverable Estimate</div>
                <span className="text-[10px] text-neutral-500 font-mono-tech ml-auto">
                  {result.ugc_summary.total_effective_views.toLocaleString()} effective views · {result.ugc_summary.total_videos.toLocaleString()} videos
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                {result.ugc_summary.per_platform.filter((p) => p.views > 0 || p.videos > 0).map((p) => (
                  <div key={p.platform} className="bg-[#0c0c0c] border border-white/5 p-4" data-testid={`ugc-${p.platform.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="text-xs text-neutral-400 mb-2 flex items-center justify-between">
                      <span>{p.platform}</span>
                      <span className="text-[9px] text-neutral-600 font-mono-tech">
                        ${p.rate_low_per_thousand_usd}–${p.rate_high_per_thousand_usd} / 1k
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-600">
                          Views {p.views_source === "assumed" && <span className="text-amber-500/80">· est.</span>}
                        </div>
                        <div className="font-mono-tech text-lg text-white">{p.effective_views.toLocaleString()}</div>
                        {p.views_source === "assumed" && (
                          <div className="text-[9px] text-neutral-600">
                            {p.videos.toLocaleString()} × {result.ugc_summary.assumed_views_per_video.toLocaleString()}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-600">Videos</div>
                        <div className="font-mono-tech text-lg text-white">{p.videos.toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-neutral-500 border-t border-white/5 pt-2 flex justify-between">
                      <span>Ø {p.avg_views_per_video.toLocaleString()} views / video</span>
                      <span className="text-amber-500/80">
                        {fmtUSD(p.est_revenue_low_usd)}–{fmtUSD(p.est_revenue_high_usd)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5">
                <div className="bg-[#0c0c0c] p-4">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Total UGC Videos</div>
                  <div className="font-display text-2xl font-medium text-white" data-testid="ugc-total-videos">
                    {result.ugc_summary.total_videos.toLocaleString()}
                  </div>
                </div>
                <div className="bg-[#0c0c0c] p-4">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Gross UGC Value (Low–High)</div>
                  <div className="font-display text-2xl font-medium text-white" data-testid="ugc-gross">
                    {fmtUSD(result.ugc_summary.gross_low_usd)} <span className="text-neutral-500 text-lg">–</span> {fmtUSD(result.ugc_summary.gross_high_usd)}
                  </div>
                </div>
                <div className="bg-[#0c0c0c] p-4">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Your Share (Low–High)</div>
                  <div className="font-display text-2xl font-medium text-amber-500" data-testid="ugc-recoverable">
                    {fmtUSD(result.ugc_summary.recoverable_low_usd)} <span className="text-neutral-500 text-lg">–</span> {fmtUSD(result.ugc_summary.recoverable_high_usd)}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed">
                {result.ugc_summary.methodology} UGC royalties are actually paid through your
                distributor&apos;s Content ID / SoundExchange partner — not this figure.
              </p>
            </div>
          )}

          {/* Royalty Ecosystem */}
          <div className="bg-[#0A0A0A] p-6">
            <div className="text-overline mb-4">Estimated Royalty Ecosystem</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {result.ecosystem.map((e) => (
                <div key={e.label} className="bg-[#0c0c0c] border border-white/5 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    {e.connected
                      ? <CheckCircle2 size={14} className="text-emerald-400" />
                      : <Circle size={14} className="text-neutral-600" />}
                    <span className="text-sm text-white">{e.label}</span>
                  </div>
                  <div className="text-xs text-neutral-500">{e.source}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Collection Readiness */}
          <div className="bg-[#0A0A0A] p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-overline">Collection Readiness</div>
              <div className="text-xs text-neutral-500 font-mono-tech">
                {result.summary.connected} connected · {result.summary.missing} missing
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {result.checks.map((c) => {
                const s = statusPill(c.status);
                const I = s.icon;
                return (
                  <div key={c.id} data-testid={`check-${c.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 bg-[#0c0c0c] border border-white/5">
                    <div className="flex items-center gap-2">
                      <I size={14} style={{ color: s.color }} />
                      <span className="text-sm text-white">{c.label}</span>
                    </div>
                    <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: s.color }}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Plan */}
          {result.action_plan.length > 0 && (
            <div className="bg-[#0A0A0A] p-6">
              <div className="text-overline mb-4">Recommended Action Plan</div>
              <ol className="space-y-3">
                {result.action_plan.map((a) => (
                  <li key={a.step} className="flex gap-4" data-testid={`analyzer-action-${a.step}`}>
                    <div className="font-display text-xl text-amber-500 w-8 shrink-0">
                      {String(a.step).padStart(2, "0")}
                    </div>
                    <div className="text-sm text-white">{a.title}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* BeatCredsi Gains */}
          <div className="bg-[#0A0A0A] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-amber-500" />
              <div className="text-overline">BeatCredsi Gain Forecast</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5">
              {[
                { label: "Potential BeatCredsi Gain",            value: result.beatcredsi_gain.overall },
                { label: "Potential Royalty Infrastructure Gain", value: result.beatcredsi_gain.royalty_infrastructure },
                { label: "Potential Metadata Authority Gain",   value: result.beatcredsi_gain.metadata_authority },
              ].map((g) => (
                <div key={g.label} className="bg-[#0c0c0c] p-5">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{g.label}</div>
                  <div className="font-display text-3xl font-medium text-amber-500" data-testid={`gain-${g.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    +{g.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer + tagline */}
          <div className="bg-[#0A0A0A] p-6">
            <p className="text-sm text-neutral-300 leading-relaxed">{result.disclaimer}</p>
            <div className="mt-3 text-overline">{result.tagline}</div>
          </div>

          {/* Save to Deep Meta Audit — persists the analyzer result as a track
              in the Deep Dive Revenue Report and kicks off a background
              MusicBrainz proactive metadata search on title × main artist. */}
          <div className="bg-[#0A0A0A] p-6 flex flex-wrap items-center justify-between gap-4 border-t border-amber-500/20">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 mb-1">
                <Save size={14} className="text-amber-500" />
                <div className="text-overline">Save to Deep Meta Audit</div>
              </div>
              {saved ? (
                <div className="text-xs text-emerald-300" data-testid="save-to-audit-confirmation">
                  ✓ Saved <span className="text-white">{saved.display_title}</span> · {saved.rows.length} platform row{saved.rows.length === 1 ? "" : "s"} · proactive metadata search running…
                </div>
              ) : (
                <p className="text-xs text-neutral-500 leading-relaxed">
                  Lock this analysis into the Deep Dive Revenue Report for
                  <span className="text-white"> {songTitle || "your track"}{songTitle && mainArtist ? " — " : ""}{mainArtist}</span>.
                  RoyAI will search MusicBrainz on the artist + title to auto-fill ISRC, label, and release year.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={saveToAudit}
              disabled={saving || !songTitle.trim() || !mainArtist.trim()}
              data-testid="save-to-audit-btn"
              className="inline-flex items-center gap-2 bg-amber-500 text-black font-semibold px-5 py-2.5 hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            >
              {saving ? (
                <><Loader2 size={14} className="animate-spin" /> Saving…</>
              ) : saved ? (
                <><CheckCircle2 size={14} /> Update Deep Meta Audit</>
              ) : (
                <>Save to Deep Meta Audit <ArrowRight size={14} /></>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
